import React, { useEffect, useState } from 'react';
import s from './Footer.module.css';
import styled from 'styled-components';
import Logo from '../../components/logo/Logo';
import DonateButton from '../../components/donateButton/DonateButton';
import SocialLinks from '../../components/socialLinks/SocialLinks';
import { fetchPage } from '../../services/pageService';
import { useLanguage } from '../../context/LanguageContext';
import { Link } from 'react-router-dom';

const NavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media (max-width: 576px) {
    gap: 8px;
  }
`;

const NavLink = styled(Link)`
  color: var(--text-in-boxes);
  text-decoration: none;
  text-align: left;
  font-size: 18px;
  font-weight: 600;
  transition: border-bottom 0.3s ease;
  padding: 8px 0;

  &:hover {
    border-bottom: 2px solid var(--text-in-boxes);
  }
`;

// Стили для модалки
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--footer-bg);
  padding: 2rem;
  width: 75%;
  max-width: 600px;
  border-radius: 10px;
  font-size: 12px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-in-boxes);
  padding: 2px 10px;
`;

interface ModalData {
  title: string;
  content_en: string;
  content_me: string;
}

const Footer: React.FC = () => {
  const { language } = useLanguage();

  const [storedLogos, setStoredLogos] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalData | null>(null);

  useEffect(() => {
    const loadPageData = async () => {
      try {
        const pageData = await fetchPage('landing');

        let parsedLogos: string[] = [];
        if (Array.isArray(pageData.partners_logos)) {
          parsedLogos = pageData.partners_logos;
        } else if (typeof pageData.partners_logos === 'string') {
          try {
            parsedLogos = JSON.parse(pageData.partners_logos);
          } catch (error) {
            console.error('Ошибка парсинга partners_logos:', error);
          }
        }
        setStoredLogos(parsedLogos);
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      }
    };

    loadPageData();
  }, [language]);

  const baseURL = 'https://novanit.org/admin-backend/public'; // Базовый URL

  // Открытие модалки
  const openModal = (type: 'privacy' | 'terms' | 'imprint') => {
    if (type === 'privacy') {
      setModalData({
        title: language === 'ME' ? 'Politika privatnosti' : 'Privacy Policy',
        content_en: `Effective date: 26.04.2025

Nova Nit ("we", "our", or "us") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and how we protect it when you visit our website https://novanit.org.

What Data We Collect:
- Personal data you provide (such as your name, email address) through contact forms.
- Automatically collected data (such as IP address, browser type, and website usage data).

How We Use Your Information:
- To respond to your inquiries and provide information about our activities.
- To improve the functionality and security of our website.
- To comply with legal obligations.

Data Sharing:
We do not sell, trade, or rent your personal data.

Cookies:
Our website may use cookies to enhance the user experience.

Your Rights:
You have the right to request access to, correction, or deletion of your personal data.

Data Protection:
We take reasonable measures to protect your data from unauthorized access, disclosure, or destruction.

Changes to the Policy:
We may update this Privacy Policy from time to time.

Contact:
novanitpg@gmail.com`,
        content_me: `Datum stupanja na snagu: 26.04.2025.

Nova Nit ("mi", "naš" ili "nas") je posvećena zaštiti vaših ličnih podataka. Ova Politika privatnosti objašnjava koje podatke prikupljamo, kako ih koristimo i kako ih štitimo kada posjetite našu web stranicu https://novanit.org.

Koje podatke prikupljamo:
- Lični podaci koje dajete (kao što su ime, adresa e-pošte) putem kontakt formi.
- Automatski prikupljeni podaci (kao što su IP adresa, tip pretraživača i podaci o upotrebi web stranice).

Kako koristimo vaše informacije:
- Da odgovorimo na vaše upite i pružimo informacije o našim aktivnostima.
- Da poboljšamo funkcionalnost i sigurnost naše web stranice.
- Da ispunimo zakonske obaveze.

Deljenje podataka:
Ne prodajemo, ne razmjenjujemo i ne iznajmljujemo vaše lične podatke.

Kolačići:
Naša web stranica može koristiti kolačiće radi poboljšanja korisničkog iskustva.

Vaša prava:
Imate pravo da zatražite pristup, ispravku ili brisanje vaših ličnih podataka.

Zaštita podataka:
Preduzimamo razumne mjere za zaštitu vaših podataka.

Promjene u politici:
Možemo povremeno ažurirati ovu Politiku privatnosti.

Kontakt:
novanitpg@gmail.com`
      });
    } else if (type === 'terms') {
      setModalData({
        title: language === 'ME' ? 'Uslovi korišćenja' : 'Terms & Conditions',
        content_en: `Effective date: 26.04.2025

Welcome to the Nova Nit website. By accessing our website, you agree to comply with these Terms and Conditions.

Use of the Website:
- The content of the website is provided for informational purposes only.
- You agree not to use the website for illegal purposes or in ways that violate laws.

Intellectual Property:
- All content on the website is the property of Nova Nit unless otherwise stated.

Limitation of Liability:
- We are not responsible for any loss or damage resulting from the use of the website.

Links to Third-Party Resources:
- We are not responsible for third-party content.

Changes to the Terms:
- We may amend these Terms and Conditions by updating this page.

Governing Law:
- These Terms are governed by the laws of Montenegro.`,
        content_me: `Datum stupanja na snagu: 26.04.2025.

Dobrodošli na web stranicu Nova Nit. Pristupom našoj stranici prihvatate ove Uslove korišćenja.

Korišćenje web stranice:
- Sadržaj stranice je informativne prirode.
- Saglasni ste da ne koristite stranicu za nezakonite svrhe.

Intelektualna svojina:
- Sav sadržaj stranice je vlasništvo Nova Nit, osim ako nije drugačije navedeno.

Ograničenje odgovornosti:
- Ne odgovaramo za bilo kakve gubitke ili štete nastale korišćenjem stranice.

Linkovi ka trećim stranama:
- Ne odgovaramo za sadržaj drugih stranica.

Promjene uslova:
- Uslovi korišćenja se mogu povremeno mijenjati.

Mjerodavno pravo:
- Uslovi su regulisani zakonima Crne Gore.`
      });
    } else if (type === 'imprint') {
      setModalData({
        title: language === 'ME' ? 'Impresum' : 'Imprint',
        content_en: `Responsible for the content according to § 5 TMG:

NU Nova Nit
8. Marta 70
81000 Podgorica
Montenegro

Contact:
Email: novanitpg@gmail.com
Phone: +382-68-067-663

Disclaimer:
We assume no liability for the content of external links. Linked site operators are solely responsible for their content.

Copyright:
All content and works on this website are subject to copyright.`,
        content_me: `Odgovorno lice za sadržaj prema § 5 TMG:

NU Nova Nit
8. Marta 70
81000 Podgorica
Crna Gora

Kontakt:
Email: novanitpg@gmail.com
Telefon: +382-68-067-663

Odricanje od odgovornosti:
Ne preuzimamo odgovornost za sadržaj eksternih linkova.

Autorska prava:
Svi sadržaji i radovi na ovoj stranici podliježu autorskim pravima.`
      });
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  return (
    <footer className={s.footer}>
      <div className={s.footerWraper}>
        <div className={s.footerWraper1}>
          <div className={s.footer_first}>
            <Logo />
            <span>
              <p>NU Nova Nit</p>
              <p>8. Marta 70</p>
              <p>81000 Podgorica</p>
              <p>Montenegro</p>
              <p>+382-68-067-663</p>
              <p>CKB 510000000012512167</p>
            </span>
          </div>

          <div className={s.footerPartnersLogos}>
            {storedLogos.length > 0 ? (
              storedLogos.map((file, idx) => (
                <div key={idx}>
                  <img
                    src={`${baseURL}${file}`}
                    alt={`Partner Logo ${idx}`}
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                </div>
              ))
            ) : (
              <p>{language === "ME" ? 'Nema dostupnih partnera.' : 'No partners available.'}</p>
            )}
          </div>
        </div>
        <div className={s.footer_third}>
          <div className={s.footer_thirdLinks}>
            <NavLinks>
              <NavLink to="/">{language === "ME" ? 'POČETNA' : 'HOME'}</NavLink>
              <NavLink to="/mission">{language === "ME" ? 'NAŠA MISIJA' : 'OUR MISSION'}</NavLink>
              <NavLink to="/faq">{language === "ME" ? 'ČESTO POSTAVLJENA PITANJA' : 'FAQ'}</NavLink>
              <NavLink to="/partners">{language === "ME" ? 'KORPORATIVNI PARTNERI' : 'CORPORATE PARTNERS'}</NavLink>
              <NavLink to="/contact-us">{language === "ME" ? 'KONTAKTIRAJTE NAS' : 'CONTACT US'}</NavLink>
              <NavLink to="/volunteer">{language === "ME" ? 'VOLONTIRAJTE' : 'VOLUNTEER'}</NavLink>
              <DonateButton />
            </NavLinks>
          </div>
          <div className={s.footer_thirdSocial}>
            <h3>{language === "ME" ? 'Prati nas' : 'Follow us'}</h3>
            <SocialLinks instagramSize={'clamp(50px, 9vw, 94px)'} facebookSize={'clamp(40px, 8vw, 84px)'} instagramUrl="https://www.instagram.com/novanit_pg/" facebookUrl="https://www.facebook.com/NovaNitPG/" />
          </div>
        </div>
      </div>

      <div className={s.footer_bottom}>
      <p style={{fontSize: '18px', color: 'var(--text-in-boxes)'}}>Made by <a className={s.footer_madeByNames} href='https://www.linkedin.com/in/elmira-hrachyan-865679314/'>  Elmira </a>&<a className={s.footer_madeByNames} href='https://www.linkedin.com/in/alina-yehorova-02aa97322/'> Alina </a> © 2025</p>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <p style={{fontSize: '18px', cursor: 'pointer'}} onClick={() => openModal('terms')} >Terms & Conditions</p>
          <p style={{fontSize: '18px', cursor: 'pointer'}} onClick={() => openModal('imprint')} >Imprint</p>
          <p style={{fontSize: '18px', cursor: 'pointer'}} onClick={() => openModal('privacy')} >Privacy Policy</p>
        </div>
      </div>

      {modalOpen && modalData && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>&times;</CloseButton>
            <h2>{modalData.title}</h2>
            <p style={{ whiteSpace: 'pre-line' }}>
              {language === 'ME' ? modalData.content_me : modalData.content_en}
            </p>
          </ModalContent>
        </ModalOverlay>
      )}
    </footer>
  );
};

export default Footer;
